import { brandTemplateDestroy } from '@goldcast/api/content';
import { Menu, Transition } from '@headlessui/react';
import { Fragment, useSyncExternalStore } from 'react';
import NewTemplateModal from '../TemplatesSideBar/NewTemplateModal';
import { PreviewTemplate } from '../types';
import useDialog from '@/components/organisms/useDialog';
import ConfirmationDialog from '@/components/organisms/ConfirmationDialog';
import ContentDialog from '@/Pages/Sessions/uiComponents/ContentDialog/ContentDialog';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import Icon, { IconName } from '@/components/atoms/Icon';
import IconButton from '@/components/atoms/Button/IconButton';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';

export default function TemplateMenu({ template }: { template: PreviewTemplate }) {
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const isRevampEnabled = !!featureFlags[FeatureFlagKeys.Use_CL_Templates_UI_Revamp];
  const {
    isOpen: isDeleteConfirmationOpen,
    openDialog: openDeleteConfirmationDialog,
    closeDialog: closeDeleteConfirmationDialog
  } = useDialog();

  const { isOpen: isEditDialogOpen, openDialog: openEditDialog, closeDialog: closeEditDialog } = useDialog();

  const contextMenu = [
    {
      iconName: (isRevampEnabled ? 'IconPencil' : 'IconEdit') as IconName,
      label: isRevampEnabled ? 'Rename' : 'Edit',
      action: event => {
        event.stopPropagation();
        openEditDialog();
      }
    },
    {
      iconName: 'IconTrash' as IconName,
      label: 'Delete',
      action: event => {
        event.stopPropagation();
        openDeleteConfirmationDialog();
      }
    }
  ];

  function deleteTemplate() {
    brandTemplateDestroy({ id: template.id as string }).then(() => {
      closeDeleteConfirmationDialog();
      EventBus.dispatch(CustomEvents.ReloadTemplates);
    });
  }

  return (
    <>
      <Menu as="div" className="absolute right-1 top-1">
        <Menu.Button
          data-testid="clip-menu-trigger"
          as="div"
          onClick={e => e.stopPropagation()}
          className="group flex size-6 shrink-0 cursor-pointer items-center justify-center hover:bg-slate-200"
        >
          <Icon name="IconDotsVertical" className="size-5 text-slate-400 group-hover:text-slate-600" size={16} />
        </Menu.Button>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute right-0 top-0 z-50 w-32 origin-top-right overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
            {contextMenu.map(menuItem => {
              const { iconName, label, action } = menuItem;

              return (
                <Menu.Item key={label}>
                  <IconButton
                    icon={iconName}
                    buttonClassName="w-full !justify-start"
                    onClick={action}
                    trackingId={`template-menu-${label.toLowerCase()}`}
                    variation="text"
                    size="base"
                    content={label}
                  />
                </Menu.Item>
              );
            })}
          </Menu.Items>
        </Transition>
      </Menu>
      <ConfirmationDialog
        isOpen={isDeleteConfirmationOpen}
        onClose={closeDeleteConfirmationDialog}
        onConfirm={deleteTemplate}
        title="Delete Template"
        content="Are you sure you want to delete this template and make it unavailable for all future clips?"
        confirmLabel="Yes"
      />
      <ContentDialog
        isOpen={isEditDialogOpen}
        setIsOpen={closeEditDialog}
        title={isRevampEnabled ? 'Rename Template' : 'Edit Template'}
        size="small"
      >
        <NewTemplateModal onSave={closeEditDialog} templateToEdit={template} hideThumbnail={isRevampEnabled} />
      </ContentDialog>
    </>
  );
}
